// src/components/communities/AcceptInvitation.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { acceptInvitation, getInvitationDetails } from '../../services/communityService';
import api from '../../services/apiService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

function AcceptInvitation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshCsrfToken, user, isAuthenticated } = useAuth();
  
  // Parse parameters from URL query string
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const communityId = queryParams.get('community_id');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [community, setCommunity] = useState(null);
  const [makePrimary, setMakePrimary] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null); // null, 'member', 'not-member'
  const [successMessage, setSuccessMessage] = useState('');
  
  useEffect(() => {
    // Check if we have all required parameters
    if (!token || !communityId) {
      setError('Manjkajoči podatki o povabilu. Preverite, če ste uporabili pravilno povezavo.');
      setLoading(false);
      return;
    }
    
    // Fetch community details using the invitation details endpoint
    async function fetchCommunity() {
      try {
        setLoading(true);
        const communityData = await getInvitationDetails(token, communityId);
        setCommunity(communityData);
        
        // If user is logged in, check if they're already in the community
        if (isAuthenticated && user) {
          try {
            const userCommunitiesResponse = await api.get('/user/communities');
            const userCommunities = userCommunitiesResponse.data || [];
            
            // Check if user is already in this community
            const communityIdNum = parseInt(communityId);
            const isMember = userCommunities.some(comm => comm.id === communityIdNum);
            setMembershipStatus(isMember ? 'member' : 'not-member');
          } catch (error) {
          }
        } else {
          setMembershipStatus('not-authenticated');
        }
      } catch (error) {
        setError('Ni mogoče najti podatkov o skupnosti. Povabilo je morda neveljavno ali je poteklo.');
      } finally {
        setLoading(false);
      }
    }
    
    fetchCommunity();
  }, [token, communityId, isAuthenticated, user]);
  
  const handleAccept = async () => {
    // Check if user is logged in
    if (!isAuthenticated) {
      // Save invitation details to localStorage and redirect to login
      localStorage.setItem('pendingInvitation', JSON.stringify({ communityId, token }));
      navigate(`/login?returnUrl=${encodeURIComponent('/invitation?token=' + token + '&community_id=' + communityId)}`);
      return;
    }
    
    try {
      setProcessing(true);
      setError('');
      
      // Refresh CSRF token before request
      if (refreshCsrfToken) {
        await refreshCsrfToken();
      }
      
      // Process the invitation
      await acceptInvitation(token, communityId, makePrimary);
      
      // Set success state
      setSuccess(true);
      setSuccessMessage(`Uspešno ste se pridružili skupnosti ${community?.name}!`);
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
      
    } catch (error) {
      setError(
        error.response?.data?.error || 
        'Napaka pri obdelavi povabila. Povabilo je morda neveljavno ali je poteklo.'
      );
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDecline = () => {
    // Just navigate back to home page
    navigate('/', { replace: true });
  };
  
  // Loading state with same style as Login page
  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center mb-4">
          <img 
            src="/MUSI.ico" 
            alt="MUSI Logo" 
            className="img-fluid" 
            style={{ maxHeight: '120px' }} 
          />
          <h2 className="mt-3">MUSI</h2>
        </div>
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Nalaganje podatkov o povabilu...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mt-5">
      <div className="text-center mb-4">
        <img 
          src="/MUSI.ico" 
          alt="MUSI Logo" 
          className="img-fluid" 
          style={{ maxHeight: '120px' }} 
        />
        <h2 className="mt-3">MUSI</h2>
      </div>
      <div className="row justify-content-center">
        <div className="col-md-6">
          {community && (
            <div className="alert alert-info mb-3">
              <strong>Povabilo v skupnost</strong>
              <p>Povabljeni ste, da se pridružite skupnosti: <strong>{community.name}</strong></p>
              {community.description && (
                <p className="small text-muted mb-0">{community.description}</p>
              )}
            </div>
          )}
          
          {success && (
            <div className="alert alert-success mb-3" role="alert">
              {successMessage}
            </div>
          )}
          
          {error && (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          )}
          
          {isAuthenticated ? (
            <div className="p-4 border rounded shadow-sm bg-white">
              {membershipStatus === 'member' ? (
                <div className="alert alert-info text-center">
                  Že ste član/ica te skupnosti.
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="make-primary" 
                        checked={makePrimary}
                        onChange={(e) => setMakePrimary(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="make-primary">
                        Nastavi kot mojo primarno skupnost
                      </label>
                      <div className="form-text text-muted">
                        Če izberete to možnost, bo to vaša glavna skupnost.
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between">
                    <button 
                      className="btn btn-outline-secondary" 
                      onClick={handleDecline}
                      disabled={processing}
                    >
                      <FontAwesomeIcon icon={faTimes} className="me-2" />
                      Zavrni
                    </button>
                    <button 
                      className="btn btn-primary" 
                      onClick={handleAccept}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Obdelava...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheck} className="me-2" />
                          Sprejmi povabilo
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-4 border rounded shadow-sm bg-white">
              <div className="alert alert-warning mb-3">
                Za pridružitev tej skupnosti se morate prijaviti ali ustvariti nov račun.
              </div>
              
              <button
                className="btn btn-primary w-100 mb-3"
                onClick={() => {
                  localStorage.setItem('pendingInvitation', JSON.stringify({ communityId, token }));
                  navigate(`/login?returnUrl=${encodeURIComponent('/invitation?token=' + token + '&community_id=' + communityId)}`);
                }}
              >
                Prijava
              </button>
              
              <div className="mt-3 text-center">
                <p>Don't have an account? <Link to={`/register?community_id=${communityId}&invitation_token=${token}`}>Register here</Link></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitation;